/**
 * Entry point and dev router (06 §8). Everything is reachable by URL so an agent can drive it:
 *
 *   /                      the game: title, operation, missions, aftermath
 *   /?quick=1              straight into the hand-built depot   &seed=1987 &drop=0 &hud=0 &pitch=45 &ppm=18
 *                          or a generated mission      &archetype= &biome= &tod= &weather= &threat=
 *   /?replay=latest        the last mission flown, played back and checked against its recorded hash
 *   /?ui=<screen>          one menu screen on fixture data, no storage (title, theatre, loadout, aftermath-kia ...)
 *   /?scene=turntable      every prop, soldier and machine on a turntable   &biome= &tod= &kind= &freeze=1 &t=
 *   /?scene=poses          every animation x 8 facings   &biome= &ppm=30 &freeze=1 &t=
 *   /?scene=lookdev        the M0 look-dev diorama     &biome= &tod= &weather= &pan=1 &freeze=1 &t=
 *                                                      &lock= &dither= &haze= &outlines=0
 *   /?anyway=1             play on a touch device regardless (there are no touch controls)
 */

import { BasicShadowMap, NoToneMapping, SRGBColorSpace, WebGLRenderer } from 'three';
import { browserMatcher, needsDesktop, OVERRIDE, showDesktopOnly } from './desktopOnly';
import { startGame } from './gameFlow';
import { startLookdev } from './lookdevApp';
import { query } from './params';
import { startPoses } from './posesApp';
import { startQuickMission } from './quickMission';
import { startReplay } from './replayApp';
import { startTurntable } from './turntableApp';
import { FIXTURE_NAMES, showFixture } from '../ui/screens';

function boot(): void {
  const canvas = document.getElementById('game') as HTMLCanvasElement;
  const renderer = new WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'high-performance' });
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = NoToneMapping;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = BasicShadowMap;
  renderer.autoClear = false;
  renderer.info.autoReset = false;

  function resize(): void {
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
  }
  window.addEventListener('resize', resize);
  resize();

  if (query.get('scene') === 'lookdev') {
    startLookdev(canvas, renderer);
  } else if (query.get('scene') === 'turntable') {
    startTurntable(canvas, renderer);
  } else if (query.get('scene') === 'poses') {
    startPoses(renderer);
  } else {
    const dev = document.getElementById('dev');
    if (dev) dev.style.display = 'none';
    const fixture = query.get('ui');
    if (fixture !== null) {
      // An unknown name lists the ones that exist, so the route documents itself.
      if (!showFixture(document.body, fixture)) document.body.append(`UNKNOWN SCREEN. TRY: ${FIXTURE_NAMES.join(', ')}`);
    } else if (query.has('replay')) startReplay(canvas, renderer);
    else if (query.has('quick')) startQuickMission(canvas, renderer);
    else startGame(canvas, renderer);
  }
}

/**
 * A phone is turned away before anything else happens (`desktopOnly.ts`): there are no touch
 * controls, so there is no way to move a ranger at all. Building a WebGL renderer and pulling a
 * campaign down first would only make the disappointment slower. `boot` is called straight from
 * the notice rather than reloading, so CONTINUE ANYWAY does not have to argue with this check.
 */
if (needsDesktop(browserMatcher) && !query.has(OVERRIDE)) showDesktopOnly(document.body, boot);
else boot();
